import { Align, Button, Justify, Label, Row } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import { CertificationPickerQuery, CertificationPickerQueryVariables, SortOrder } from '__generated__/graphql';
import classNames from 'classnames';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import ManageCertificationDialog from 'containers/manage-certifications/ManageCertificationsDialog/ManageCertificationsDialog';
import gql from 'graphql-tag';
import { useOpenable } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { t } from 'i18next';
import { first } from 'lodash';
import * as React from 'react';
import { ReactNode, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { useFeatureFlags } from 'utils/features';

export interface ICertificationPickerProps {
  value: string | null;
  onSelect: (certificationId: string | null) => void;
  placeholder?: string;
  error?: boolean;
  className?: ClassName;
  dropDownButtonRender?: ReactNode;
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
  allowManage?: boolean;
  label?: string;
}

type CertificationPickerCertification = NonNullArrayField<CertificationPickerQuery['certifications']>;

function CertificationPicker(props: ICertificationPickerProps) {
  const {
    value,
    onSelect,
    error,
    placeholder,
    closeButtonRender,
    dropDownButtonRender,
    allowManage,
    label,
    className,
  } = props;

  const isCertificationStatusEnabled = useFeatureFlags('CERTIFICATIONS');

  const graphqlClient = useGraphQLClient();

  const [isOpen, setIsOpen] = useState(false);

  function onClose() {
    setIsOpen(false);
  }

  const baseQueryKey = useReactQueryBaseKey();

  const { data: selectedCertification, refetch: refetchSelectedCertification } = useQuery({
    queryKey: [...baseQueryKey, 'certification-by-id', value],
    queryFn: async () => {
      if (!value) {
        return null;
      }

      const result = await graphqlClient.request<CertificationPickerQuery, CertificationPickerQueryVariables>({
        document: CERTIFICATION_PICKER_QUERY,
        variables: {
          filter: { id: { equal: value }, deletedOn: { isNull: true } },
        },
      });

      const target = first(result?.certifications);
      return target?.name ?? null;
    },
    initialData: null,
    enabled: !!value,
  });

  const variables: CertificationPickerQueryVariables = {
    filter: {
      deletedOn: { isNull: true },
    },
    sort: [{ name: SortOrder.Asc }],
  };

  async function getCertifications(after: string | null, first: number): Promise<CertificationPickerCertification[]> {
    const pagedVariables: CertificationPickerQueryVariables = {
      after,
      first,
      ...variables,
    };

    const result = await graphqlClient.request<CertificationPickerQuery, CertificationPickerQueryVariables>({
      document: CERTIFICATION_PICKER_QUERY,
      variables: pagedVariables,
    });

    return result.certifications ?? [];
  }

  const scroller = React.useRef<HTMLElement>();

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
    refetch: refetchCertifications,
  } = useReactQueryLazyLoading(
    scroller.current,
    [...baseQueryKey, 'certification-picker', variables],
    getCertifications,
    10,
    {
      enabled: isOpen,
    }
  );

  const manageCertificationsDialog = useOpenable();

  function handleSelect(model: CertificationPickerCertification | null) {
    return onSelect(model ? model.id : null);
  }

  function renderValueTemplate() {
    if (selectedCertification) {
      return <>{selectedCertification}</>;
    }

    return <></>;
  }

  function renderRow(certification: CertificationPickerCertification) {
    return <SimplePickerRow value={certification.name} />;
  }

  const classes = classNames('certification-picker', className);

  if (!isCertificationStatusEnabled) {
    return null;
  }

  return (
    <>
      <Row align={Align.START} justify={Justify.SPACE_BETWEEN}>
        <Label className="mt-3">{label ?? t('Certification')}</Label>
        {allowManage && (
          <Button type="link" onClick={manageCertificationsDialog.open}>
            {t('Manage')}
          </Button>
        )}
      </Row>
      <DropDownPicker
        value={value}
        dataError={isDataError}
        error={error}
        data={data ?? []}
        loadedAll={loadedAll}
        onSelect={handleSelect}
        onClose={onClose}
        onOpen={() => setIsOpen(true)}
        valueTemplate={renderValueTemplate()}
        renderRow={renderRow}
        placeholder={placeholder}
        className={classes}
        closeButtonRender={closeButtonRender}
        dropDownButtonRender={dropDownButtonRender}
        setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
        isLoading={isLoading}
        disabled={false}
      />
      <ManageCertificationDialog
        isOpen={manageCertificationsDialog.isOpen}
        onClose={async () => {
          manageCertificationsDialog.close();
          await Promise.all([refetchCertifications(), refetchSelectedCertification()]);
        }}
      />
    </>
  );
}

export default CertificationPicker;

export const CERTIFICATION_PICKER_QUERY = gql`
  query CertificationPicker($after: String, $first: Int, $filter: CertificationFilter, $sort: [CertificationSort!]) {
    certifications(after: $after, first: $first, filter: $filter, sort: $sort) {
      id
      cursor
      name
      description
      createdOn
      updatedOn
      deletedOn
    }
  }
`;
